// 📄 services/credentialService.ts
// Vault de credenciales del proyecto — el frontend NUNCA escribe directo
// en Firestore (ver firestore.rules: projects/{id}/credenciales solo
// permite `read`, todo `write` pasa por la Cloud Function
// `credentialManager`, que coordina Firestore + Secret Manager). Mismo
// patrón de httpsCallable que createProjectMemberAccount en
// projectService.ts.
import { httpsCallable } from 'firebase/functions';
import { collection, onSnapshot, or, orderBy, query, Timestamp, Unsubscribe, where } from 'firebase/firestore';
import { db, functionsClient } from '../firebase';

export type CredentialAmbiente = 'DEV' | 'QAS' | 'PRD' | 'OTRO';

export interface CredentialMetadata {
  id: string;
  nombre: string;
  url: string;
  usuario: string;
  ambiente: CredentialAmbiente;
  nota: string;
  compartida: boolean;
  creadoPor: string;
  creadoPorNombre: string;
  // Legado: referencia a Secret Manager de credenciales viejas. Desde la
  // Fase 4 de plan-kms-credenciales-v1 la contraseña va cifrada en un
  // documento aparte y este campo ya no existe en las nuevas.
  secretId?: string;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
}

export interface CredentialInput {
  nombre: string;
  url: string;
  usuario: string;
  password: string; // obligatoria en create; vacía en update = no se cambia
  ambiente: CredentialAmbiente;
  nota: string;
  compartida: boolean;
}

// Solo lectura directa (write: if false en firestore.rules, ver ese
// archivo). Las rules exigen creadoPor == uid || compartida == true, y
// Firestore NO puede autorizar un list()/onSnapshot() sin filtro contra
// una regla así — deniega la query completa (verificado con el emulador:
// ver PR de este cambio). Por eso la query en sí ya trae el mismo OR vía
// query.or(), no un list() plano — una credencial Privada ajena nunca
// llega a este listener, ni sus metadatos. El filtro "Mis credenciales" /
// "Compartidas conmigo" en CredentialsPanel.tsx solo subdivide ESE
// conjunto (ya reducido acá), no es una segunda capa de seguridad.
export const subscribeCredentials = (
  projectId: string,
  uid: string,
  callback: (credentials: CredentialMetadata[]) => void
): Unsubscribe => {
  const q = query(
    collection(db, 'projects', projectId, 'credenciales'),
    or(where('creadoPor', '==', uid), where('compartida', '==', true)),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<CredentialMetadata, 'id'>) })));
  });
};

const credentialManager = httpsCallable(functionsClient, 'credentialManager');

export async function createCredential(projectId: string, data: CredentialInput): Promise<{ credId: string }> {
  const result = await credentialManager({ action: 'create', projectId, data });
  return result.data as { credId: string };
}

export async function updateCredential(projectId: string, credId: string, data: CredentialInput): Promise<void> {
  await credentialManager({ action: 'update', projectId, credId, data });
}

export async function deleteCredential(projectId: string, credId: string): Promise<void> {
  await credentialManager({ action: 'delete', projectId, credId });
}

// La contraseña ya no se lee por credentialManager (R5 de plan-revelado-credenciales):
// se ve o se pide con credencialAccesoService.ts, con re-autenticación e historial.
